import { ringSegments } from "./chart-math";

// A ring of segments for the shares of a whole (Paper "What <customer> asks for"). Each segment is a circle drawn with `stroke-dasharray`, a
// palette colour each, no gradient. The ring is decoration (`aria-hidden`): the legend next to it writes every name and number as text.

export type DonutSlice = Readonly<{
  key: string;
  label: string;
  count: number;
  /** The stroke class of the segment. */
  className: string;
  /** The fill class of the same colour, for the legend square. */
  fillClassName: string;
}>;

const SIZE = 120;
const RADIUS = 48;
const STROKE = 16;

export function Donut({
  slices,
  centre,
}: Readonly<{
  slices: readonly DonutSlice[];
  centre: Readonly<{ value: string; caption: string }>;
}>) {
  const segments = ringSegments(
    slices.map((slice) => slice.count),
    RADIUS,
  );
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="size-[120px] shrink-0"
    >
      <circle
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        fill="none"
        className="stroke-muted"
        strokeWidth={STROKE}
      />
      {slices.map((slice, index) => {
        const segment = segments[index];
        if (segment === undefined || slice.count === 0) return null;
        return (
          <circle
            key={slice.key}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            className={slice.className}
            strokeWidth={STROKE}
            strokeDasharray={`${segment.dash} ${segment.gap}`}
            strokeDashoffset={segment.offset}
          />
        );
      })}
      <text
        x={SIZE / 2}
        y={SIZE / 2 + 2}
        textAnchor="middle"
        className="fill-foreground font-serif text-[26px]"
      >
        {centre.value}
      </text>
      <text
        x={SIZE / 2}
        y={SIZE / 2 + 18}
        textAnchor="middle"
        className="fill-muted-foreground text-[10px]"
      >
        {centre.caption}
      </text>
    </svg>
  );
}
